import { useState, useEffect, useCallback } from 'react';

/**
 * Callback-ref hook that detects whether a scrollable container has
 * hidden content below the visible area. Returns a `ref` callback
 * (safe for conditionally-rendered elements) and a `showHint` boolean.
 */
export function useScrollIndicator(): { ref: (node: HTMLElement | null) => void; showHint: boolean } {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [showHint, setShowHint] = useState(false);

  const ref = useCallback((node: HTMLElement | null) => {
    setEl(node);
  }, []);

  useEffect(() => {
    if (!el) {
      setShowHint(false);
      return;
    }

    function check() {
      if (!el) return;
      const { scrollHeight, clientHeight, scrollTop } = el;
      // Show hint only when there's real overflow AND user is at the top.
      // Hides as soon as scrolling starts; reappears if scrolled back to top.
      setShowHint(scrollHeight > clientHeight + 4 && scrollTop < 2);
    }

    check();

    el.addEventListener('scroll', check, { passive: true });

    const ro = new ResizeObserver(check);
    ro.observe(el);

    const mo = new MutationObserver(check);
    mo.observe(el, { childList: true, subtree: true, characterData: true });

    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
      mo.disconnect();
    };
  }, [el]);

  return { ref, showHint };
}
